import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';
import Slider from 'react-slick';
import { motion } from 'framer-motion';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import staffingimg from '../Assest/cards/permanent-staffing-company.avif';

// coreBenefits list card images
import workforceScalabilityImg from '../Assest/cards/why merida hr/1/Workforce Scalability Without Operational Breakdown.avif';
import fasterHiringImg from '../Assest/cards/why merida hr/1/Faster Hiring Without Sacrificing Workforce Quality.avif';
import integratedOpsImg from '../Assest/cards/why merida hr/1/Integrated Workforce Operations Instead Of Fragmented HR Systems.avif';
import payrollComplianceImg from '../Assest/cards/why merida hr/1/Payroll & Compliance Confidence At Scale.avif';
import industrySpecializedRecruitmentImg from '../Assest/cards/why merida hr/1/Industry-Specialized Recruitment Intelligence.avif';
import longTermPartnershipImg1 from '../Assest/cards/why merida hr/1/Long-Term Workforce Partnership Instead Of Recruitment Transactions.avif';

// howWeOperate list card images
import aiRecruitmentImg2 from '../Assest/cards/why merida hr/2/AI-Assisted Recruitment Intelligence.avif';
import workforceInfraImg2 from '../Assest/cards/why merida hr/2/Workforce Infrastructure Thinking.avif';
import integratedEcosystemImg2 from '../Assest/cards/why merida hr/2/Integrated Workforce Ecosystem.avif';
import modernBizImg2 from '../Assest/cards/why merida hr/2/Built For Modern Business Environments.avif';
import industrySpecializedHiringImg2 from '../Assest/cards/why merida hr/2/Industry-Specialized Hiring Expertise.avif';
import longTermPartnershipModelImg2 from '../Assest/cards/why merida hr/2/Long-Term Workforce Partnership Model.avif';

import benefit1 from '../Assest/cards/why merida hr/1/Workforce Scalability Without Operational Breakdown.avif';
import benefit2 from '../Assest/cards/why merida hr/1/Faster Hiring Without Sacrificing Workforce Quality.avif';
import benefit3 from '../Assest/cards/why merida hr/1/Integrated Workforce Operations Instead Of Fragmented HR Systems.avif';
import benefit4 from '../Assest/cards/why merida hr/1/Payroll & Compliance Confidence At Scale.avif';
import benefit5 from '../Assest/cards/why merida hr/1/Industry-Specialized Recruitment Intelligence.avif';
import benefit6 from '../Assest/cards/why merida hr/1/Long-Term Workforce Partnership Instead Of Recruitment Transactions.avif';
import execMeeting from '../Assest/cards/why merida hr/1/Executive Strategy Meeting (BANNER).avif';

import operate1 from '../Assest/cards/why merida hr/2/AI-Assisted Recruitment Intelligence.avif';
import operate2 from '../Assest/cards/why merida hr/2/Workforce Infrastructure Thinking.avif';
import operate3 from '../Assest/cards/why merida hr/2/Integrated Workforce Ecosystem.avif';
import operate4 from '../Assest/cards/why merida hr/2/Built For Modern Business Environments.avif';
import operate5 from '../Assest/cards/why merida hr/2/Industry-Specialized Hiring Expertise.avif';
import operate6 from '../Assest/cards/why merida hr/2/Long-Term Workforce Partnership Model.avif';

import {
    ArrowRight, CheckCircle2, Shield, Zap, Target,
    BarChart3, Users, Settings, Activity, Cpu,
    Award, Network, Layers, Scale, XCircle, Sparkles, HelpCircle
} from 'lucide-react';
import { GlobalEnquiryContext } from '../App';
import CTAButton from '../Component/CTAButton';

const WhyMeridaPage = () => {
    const navigate = useNavigate();
    const { openEnquiry } = useContext(GlobalEnquiryContext);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const openEnquiryModal = () => {
        openEnquiry("Why Merida HR Page Request");
    };

    const comparisonList = [
        { traditional: "Vacancy-focused hiring", merida: "Workforce transformation consulting" },
        { traditional: "Resume-forward sourcing", merida: "Strategic talent alignment" },
        { traditional: "Generic recruitment support", merida: "Industry-specialized workforce expertise" },
        { traditional: "Fragmented HR services", merida: "Integrated workforce ecosystem" },
        { traditional: "Manual recruitment execution", merida: "AI-assisted recruitment operations" },
        { traditional: "Short-term staffing support", merida: "Long-term workforce scalability" },
        { traditional: "Hiring-focused engagement", merida: "Operational workforce consulting" },
        { traditional: "Reactive HR processes", merida: "Structured workforce systems" },
        { traditional: "Limited visibility", merida: "Workforce intelligence & planning" }
    ];

    const coreBenefits = [
        {
            title: "Workforce Scalability Without Operational Breakdown",
            desc: "Many businesses grow revenue faster than workforce infrastructure. Without scalable hiring systems, organizations eventually experience recruitment bottlenecks, workforce inconsistency, operational overload, and expansion inefficiencies.",
            img: benefit1,
            points: [
                "Scalable hiring infrastructure",
                "Structured workforce planning",
                "Multi-location recruitment capability",
                "High-volume hiring support",
                "Workforce visibility during expansion",
                "Operational hiring consistency"
            ]
        },
        {
            title: "Faster Hiring Without Sacrificing Workforce Quality",
            desc: "Unstructured recruitment processes often create delayed hiring cycles, inconsistent candidate quality, and operational workforce gaps. Our AI-assisted recruitment systems improve hiring coordination, candidate alignment, recruitment visibility, and sourcing efficiency.",
            img: benefit2,
            points: [
                "Faster recruitment execution",
                "Reduced screening delays",
                "Improved candidate-role alignment",
                "Better recruitment visibility",
                "Structured hiring coordination",
                "Enhanced workforce quality"
            ]
        },
        {
            title: "Integrated Workforce Operations Instead Of Fragmented HR Systems",
            desc: "Most organizations manage recruitment, payroll, onboarding, compliance, HR administration, and workforce operations separately. As businesses grow, this fragmentation creates operational inefficiency and workforce instability.",
            img: benefit3,
            points: [
                "Workforce process integration",
                "Operational HR efficiency",
                "Streamlined workforce coordination",
                "Reduced administrative complexity",
                "Better workforce visibility",
                "Structured HR operations"
            ]
        },
        {
            title: "Payroll & Compliance Confidence At Scale",
            desc: "Payroll inaccuracies and compliance gaps create increasing operational and financial risk as workforce size expands. Our workforce systems help businesses strengthen workforce governance and compliance consistency.",
            img: benefit4,
            points: [
                "Accurate payroll operations",
                "Statutory compliance support",
                "PF & ESI coordination",
                "Contractor payroll management",
                "Workforce documentation systems",
                "Compliance governance structure"
            ]
        },
        {
            title: "Industry-Specialized Recruitment Intelligence",
            desc: "Every industry operates with unique hiring patterns, workforce expectations, operational structures, and recruitment challenges. Our industry-specialized recruitment teams understand these workforce ecosystems deeply.",
            img: benefit5,
            points: [
                "IT & Software",
                "BFSI",
                "Healthcare",
                "Manufacturing",
                "Ecommerce",
                "Logistics & Supply Chain",
                "Startups",
                "GCCs"
            ]
        },
        {
            title: "Long-Term Workforce Partnership Instead Of Recruitment Transactions",
            desc: "Traditional staffing agencies often focus on short-term hiring activity. Merida HR Consulting focuses on building workforce ecosystems capable of supporting sustainable organizational growth.",
            img: benefit6,
            points: [
                "Long-term workforce strategy",
                "Recruitment process maturity",
                "Workforce continuity",
                "Operational hiring structure",
                "Scalable HR systems",
                "Strategic workforce planning"
            ]
        }
    ];

    const strategicPillars = [
        { title: "Workforce Intelligence", desc: "Data-supported recruitment coordination, hiring visibility, sourcing efficiency, and workforce planning systems designed for smarter workforce execution.", icon: BrainIcon },
        { title: "Scalability", desc: "Workforce infrastructure designed to support startup expansion, enterprise workforce growth, GCC scaling, and multi-location hiring operations.", icon: Network },
      { title: "Operational Efficiency", desc: (
            <>
                Integrated workforce systems reducing recruitment delays, <a href="https://nativeteams.com/blog/global-payroll-complexity" target="_blank" rel="noreferrer" className="text-blue-600 underline">payroll complexity</a>, administrative overload, and HR operational friction.
            </>
        ), icon: Settings },
        { title: "Workforce Quality", desc: "Strategic talent alignment focused on workforce consistency, candidate relevance, long-term fit, and hiring precision.", icon: Award },
        { title: "Workforce Governance", desc: "Payroll accuracy, statutory compliance, contractor management, HR documentation, and workforce process structure designed for operational reliability.", icon: Scale },
        { title: "Long-Term Workforce Strategy", desc: "Structured workforce systems supporting organizational continuity, operational clarity, and sustainable workforce growth.", icon: Target }
    ];

    const howWeOperate = [
        { title: "AI-Assisted Recruitment Intelligence", desc: "Technology-enabled sourcing, screening, candidate coordination, and recruitment workflows designed for faster and more structured hiring execution.", img: operate1 },
        { title: "Workforce Infrastructure Thinking", desc: "We focus on building scalable workforce systems instead of simply managing recruitment transactions.", img: operate2 },
        { title: "Integrated Workforce Ecosystem", desc: "Staffing + Payroll + Compliance + Workforce Operations + HR Administration under one strategic workforce framework.", img: operate3 },
        { title: "Built For Modern Business Environments", desc: "Designed specifically for startups, enterprises, GCCs, and rapidly growing organizations.", img: operate4 },
        { title: "Industry-Specialized Hiring Expertise", desc: "Recruitment teams aligned with sector-specific workforce challenges and hiring ecosystems.", img: operate5 },
        { title: "Long-Term Workforce Partnership Model", desc: "Focused on sustainable organizational growth instead of short-term staffing engagement.", img: operate6 }
    ];

    const whoBenefits = [
        { title: "Startups Scaling Teams Rapidly", desc: "Building scalable workforce systems capable of supporting aggressive growth.", icon: Zap },
        { title: "Enterprises Managing Workforce Complexity", desc: "Improving workforce visibility, operational structure, and recruitment scalability.", icon: Layers },
        { title: "GCCs Expanding Workforce Operations", desc: "Supporting workforce expansion, specialized hiring, and scalable recruitment operations.", icon: Network },
        { title: "Businesses Facing Hiring Bottlenecks", desc: "Reducing recruitment delays and improving hiring coordination.", icon: Activity },
        { title: "Organizations Managing Payroll & Compliance Complexity", desc: "Strengthening workforce governance and operational HR structure.", icon: Scale },
        { title: "Companies Transitioning Beyond Traditional Staffing", desc: "Building integrated workforce ecosystems for sustainable growth.", icon: Cpu }
    ];

    const businessOutcomes = [
        { title: "Faster Hiring Execution", desc: "Structured recruitment systems reducing hiring delays and operational workforce gaps." },
        { title: "Better Hiring Accuracy", desc: "Role-aligned recruitment improving workforce productivity and organizational fit." },
        { title: "Workforce Scalability", desc: "High-volume hiring infrastructure supporting rapid business expansion." },
        { title: "Reduced Operational HR Complexity", desc: "Integrated workforce systems simplifying HR coordination and administration." },
        { title: "Payroll & Compliance Reliability", desc: "Improved workforce governance and reduced operational workforce risk." },
        { title: "Long-Term Organizational Workforce Stability", desc: "Strategic workforce systems supporting sustainable business growth." }
    ];

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        pauseOnHover: true,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1 } }
        ]
    };

    return (
        <div className="bg-[#01014C] text-white min-h-screen relative">
           <Helmet>
    {/* Basic SEO */}
    <title>Why Choose Merida HR Consulting | Workforce Growth Experts</title>

    <meta
        name="description"
        content="Discover why businesses choose Merida HR Consulting for scalable hiring, workforce consulting, payroll outsourcing, compliance, and AI-powered recruitment solutions."
    />

    <meta
        name="keywords"
        content="Why Choose Merida HR Consulting, best HR consulting company in Bangalore, top HR consulting firms in Bangalore, HR consulting company in Jayanagar, strategic HR consulting company, AI-powered HR consulting services, staffing and recruitment company, HR outsourcing company in Bangalore, recruitment consulting firm in Bangalore, workforce management company in Bangalore, RPO services company in Bangalore, payroll outsourcing company in Bangalore, HR compliance services company, staffing solutions company, executive search firms in Bangalore, contract staffing company, permanent staffing company, high-volume hiring company, startup hiring consulting company, GCC hiring company in Bangalore, AI recruitment solutions company, talent acquisition consulting company, best staffing company in Bangalore, HR solutions company near me, staffing agency near me, top recruitment firms in Bangalore, strategic workforce planning company, IT staffing company in Bangalore, non-IT staffing company in Bangalore, corporate HR solutions company, recruitment process outsourcing company, end-to-end HR solutions company, best HR consulting company in Bangalore for scalable workforce growth, AI-powered recruitment and staffing company for enterprises, top workforce consulting company for GCC hiring in Bangalore, reliable payroll outsourcing and compliance company for growing businesses, strategic workforce consulting company for startups and enterprises, HR consulting company with integrated payroll and recruitment solutions, top recruitment consulting firm in Bangalore for operational workforce scaling, best staffing agency near me for high-volume hiring support, workforce management company in Bangalore for long-term organizational growth, contract staffing company with workforce compliance solutions, end-to-end recruitment outsourcing company for modern businesses, AI recruitment solutions company for scalable hiring operations, HR outsourcing company in Bangalore for workforce transformation, top HR consulting agency in Jayanagar for business growth"
    />

    <link
        rel="canonical"
        href="https://www.meridahr.com/why-merida"
    />

    {/* Open Graph */}
    <meta property="og:type" content="website" />
    <meta
        property="og:title"
        content="Why Choose Merida HR Consulting | Workforce Growth Experts"
    />
    <meta
        property="og:description"
        content="Discover why businesses choose Merida HR Consulting for scalable hiring, workforce consulting, payroll outsourcing, compliance, and AI-powered recruitment solutions."
    />
    <meta
        property="og:url"
        content="https://www.meridahr.com/why-merida"
    />
    <meta
        property="og:image"
        content="https://www.meridahr.com/images/why-merida-banner.webp"
    />

    {/* Twitter */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta
        name="twitter:title"
        content="Why Choose Merida HR Consulting | Workforce Growth Experts"
    />
    <meta
        name="twitter:description"
        content="Discover why businesses choose Merida HR Consulting for scalable hiring, workforce consulting, payroll outsourcing, compliance, and AI-powered recruitment solutions."
    />
    <meta
        name="twitter:image"
        content="https://www.meridahr.com/images/why-merida-banner.webp"
    />

    {/* Organization */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",

            "@id": "https://www.meridahr.com/#organization",

            name: "Merida HR Consulting",

            url: "https://www.meridahr.com",

            logo: {
                "@type": "ImageObject",
                "@id": "https://www.meridahr.com/#logo",
                url: "https://www.meridahr.com/logo.avif",
                contentUrl: "https://www.meridahr.com/logo.avif",
                caption: "Merida HR Consulting"
            },

            image: {
                "@id": "https://www.meridahr.com/#logo"
            },

            email: "meridahrconsulting@gmail.com",

            telephone: "+918904599283",

            address: {
                "@type": "PostalAddress",
                addressLocality: "Bengaluru",
                addressRegion: "Karnataka",
                addressCountry: "IN"
            },

            sameAs: [
                "https://www.facebook.com/MERIDAHRCONSULTING/",
                "https://www.linkedin.com/company/95719869/admin/dashboard/",
                "https://www.instagram.com/meridahrconsultancy/?hl=en",
                "https://www.youtube.com/@meridahrconsultancy5069"
            ],

            contactPoint: [
                {
                    "@type": "ContactPoint",
                    telephone: "+918904599283",
                    contactType: "Customer Support",
                    availableLanguage: ["English"]
                }
            ]
        })}
    </script>

    {/* Website */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",

            "@type": "WebSite",

            "@id": "https://www.meridahr.com/#website",

            url: "https://www.meridahr.com",

            name: "Merida HR Consulting",

            publisher: {
                "@id": "https://www.meridahr.com/#organization"
            },

            inLanguage: "en-US",

            potentialAction: {
                "@type": "SearchAction",
                target:
                    "https://www.meridahr.com/search?q={search_term_string}",
                "query-input":
                    "required name=search_term_string"
            }
        })}
    </script>

    {/* Why Merida Page */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",

            "@type": "AboutPage",

            "@id": "https://www.meridahr.com/why-merida",

            url: "https://www.meridahr.com/why-merida",

            name: "Why Choose Merida HR Consulting",

            description:
                "Discover what sets Merida HR Consulting apart as a trusted HR consulting and staffing partner with expertise in AI-powered recruitment, workforce consulting, payroll outsourcing, compliance, and scalable hiring solutions.",

            isPartOf: {
                "@id": "https://www.meridahr.com/#website"
            },

            about: {
                "@id": "https://www.meridahr.com/#organization"
            },

            publisher: {
                "@id": "https://www.meridahr.com/#organization"
            },

            primaryImageOfPage: {
                "@id":
                    "https://www.meridahr.com/why-merida/#primaryimage"
            },

            image: {
                "@id":
                    "https://www.meridahr.com/why-merida/#primaryimage"
            },

            breadcrumb: {
                "@id":
                    "https://www.meridahr.com/why-merida/#breadcrumb"
            },

            inLanguage: "en-US",

           

            potentialAction: {
                "@type": "ReadAction",
                target: [
                    "https://www.meridahr.com/why-merida"
                ]
            }
        })}
    </script>

    {/* Primary Image */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",

            "@type": "ImageObject",

            "@id":
                "https://www.meridahr.com/why-merida/#primaryimage",

            inLanguage: "en-US",

            url:
                "https://www.meridahr.com/images/why-merida-banner.webp",

            contentUrl:
                "https://www.meridahr.com/images/why-merida-banner.webp",

            caption:
                "Why Choose Merida HR Consulting"
        })}
    </script>

    {/* Breadcrumb */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",

            "@type": "BreadcrumbList",

            "@id":
                "https://www.meridahr.com/why-merida/#breadcrumb",

            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: "https://www.meridahr.com/"
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: "Why Merida",
                    item: "https://www.meridahr.com/why-merida"
                }
            ]
        })}
    </script>
</Helmet>

            <div className="absolute w-full top-0 left-0 z-50">
                <HomeNavBar type="white" />
            </div>

            {/* HERO SECTION */}
                      {/* HERO SECTION */}
            <section className="relative pt-36 pb-24 overflow-hidden bg-[#01014C]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(59,130,246,0.18),transparent_60%)]" />
                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 text-left space-y-6">
                            <span className="text-blue-400 font-black tracking-[0.3em] text-[10px] uppercase block">Strategic HR Alignment</span>
                            <div className="space-y-3">
                                <h1 className="text-3xl md:text-5xl font-bold syne leading-tight text-white">
                                    Why Modern Businesses Choose Merida HR?
                                </h1>
                                <h2 className="text-xl md:text-2xl font-medium syne text-blue-300">
                                    Consulting Beyond Traditional Recruitment
                                </h2>
                            </div>
                            <p className="text-blue-100/70 text-base md:text-lg leading-relaxed font-light">
                                Modern workforce challenges demand far more than traditional recruitment support. Businesses today require scalable hiring systems, operational workforce clarity, <a href="/service/payroll-compliance" className="text-blue-600 underline">payroll governance</a>, compliance management, recruitment intelligence, and workforce strategies capable of supporting long-term organizational growth.
                            </p>
                            <div className="flex flex-wrap gap-4 pt-2">
                                <CTAButton
                                    text="Book Strategic Workforce Consultation"
                                    onClick={openEnquiryModal}
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-5 relative">
                            {/* Glass decorative background glows */}
                            <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2.5rem] blur opacity-25" />
                            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 shadow-2xl bg-gradient-to-br from-blue-950/80 to-[#01014C]/80 aspect-[4/3] lg:aspect-[5/4]">
                                <img
                                    src="/strategic_workforce_consulting.png"
                                    alt="Merida HR Strategic Workforce Consultation"
                                    className="w-full h-full object-cover opacity-95 hover:scale-105 hover:opacity-100 transition-all duration-500"
                                />
                                {/* Floating stat card 1 */}
                                {/* <div className="absolute bottom-6 left-6 p-4 bg-[#01014c]/70 backdrop-blur-md border border-white/10 rounded-2xl shadow-xl flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-500/20 text-blue-400 flex items-center justify-center rounded-lg">
                                        <Target className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-blue-300/80 uppercase tracking-widest font-bold">Accuracy</p>
                                        <p className="text-sm font-bold text-white">98% Sourcing Match</p>
                                    </div>
                                </div> */}

                                {/* Floating stat card 2 */}
                                {/* <div className="absolute top-6 right-6 p-4 bg-[#01014c]/70 backdrop-blur-md border border-white/10 rounded-2xl shadow-xl flex items-center gap-3">
                                    <div className="w-8 h-8 bg-emerald-500/20 text-emerald-400 flex items-center justify-center rounded-lg">
                                        <Zap className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-emerald-300/80 uppercase tracking-widest font-bold">Execution</p>
                                        <p className="text-sm font-bold text-white">2.5x Faster Sourcing</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* COMPARISON SECTION */}
            <section className="py-10 bg-white text-slate-800 relative">
                <div className="container mx-auto px-6">
                    <div className="max-w-5xl mx-auto text-center mb-16">
                        <span className="text-blue-600 font-bold text-xs uppercase block mb-3">The Paradigm Shift</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne text-[#01014C] mb-6">
                            The Reason Businesses Outgrow Traditional Recruitment Agencies
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                            Most recruitment agencies were built for a business environment that no longer exists. Today’s organizations are not simply hiring employees — they are managing aggressive business expansion, workforce scalability pressure, leadership hiring, operational HR complexity, payroll governance, workforce continuity, compliance management, and organizational efficiency at scale.
                        </p>
                    </div>

                    {/* NEW INFOGRAPHIC DESIGN */}
                    <div className="max-w-5xl mx-auto bg-[#01014C] rounded-[2.5rem] p-8 md:p-12 shadow-2xl relative overflow-hidden">
                        
                        {/* CENTER "COMPARISON" DIVIDER */}
                        <div className="hidden md:flex absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-[2px] flex-col items-center justify-center z-10">
                            {/* Center white line */}
                            <div className="absolute inset-y-0 w-[2px] bg-white/40" />
                            
                            <div className="bg-[#01014C] py-8 z-10 relative">
                                <div className="text-transparent font-black tracking-[0.4em] text-4xl uppercase" 
                                     style={{ 
                                         writingMode: 'vertical-rl', 
                                         transform: 'rotate(180deg)',
                                         WebkitTextStroke: '1px rgba(255,255,255,0.8)'
                                     }}>
                                    COMPARISON
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12 relative z-20 mt-6">
                            
                            {/* LEFT COLUMN - TRADITIONAL */}
                            <div className="relative">
                                {/* Slate outer border */}
                                <div className="hidden md:block absolute -top-10 -bottom-6 -left-6 right-0 border-t-2 border-l-2 border-b-2 border-slate-500 rounded-l-3xl z-0 pointer-events-none" />
                                {/* White connecting border */}
                                <div className="hidden md:block absolute -top-6 -right-12 left-6 border-t-2 border-white/40 rounded-tl-3xl z-0 pointer-events-none" />

                                <div className="flex justify-center md:justify-center mb-10 relative z-10">
                                    <div className="bg-slate-700 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg border border-slate-600 whitespace-nowrap tracking-wide">
                                        TRADITIONAL
                                    </div>
                                </div>

                                <div className="space-y-8 relative z-10">
                                    {comparisonList.slice(0, 6).map((item, i) => (
                                        <div key={i} className="flex justify-start md:justify-center items-start gap-3">
                                            <div className="w-5 h-5 bg-slate-500 mt-1 flex-shrink-0 rounded-sm" />
                                            <div className="text-left mt-0.5 w-full max-w-[280px]">
                                                {/* <h4 className="text-slate-400 font-bold text-sm mb-1 uppercase ">Point 0{i+1}</h4> */}
                                                <p className="text-slate-300 text-sm font-medium ">{item.traditional}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* RIGHT COLUMN - MERIDA */}
                            <div className="relative">
                                {/* Blue outer border */}
                                <div className="hidden md:block absolute -top-10 -bottom-6 -right-6 left-0 border-t-2 border-r-2 border-b-2 border-blue-500 rounded-r-3xl z-0 pointer-events-none" />
                                {/* White connecting border */}
                                <div className="hidden md:block absolute -top-6 -left-12 right-6 border-t-2 border-white/40 rounded-tr-3xl z-0 pointer-events-none" />

                                <div className="flex justify-center md:justify-center mb-10 relative z-10">
                                    <div className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg border border-blue-500 whitespace-nowrap tracking-wide">
                                        MERIDA HR
                                    </div>
                                </div>

                                <div className="space-y-8 relative z-10">
                                    {comparisonList.slice(0, 6).map((item, i) => (
                                        <div key={i} className="flex justify-start md:justify-center items-start gap-3">
                                            <div className="w-5 h-5 bg-blue-500 mt-1 flex-shrink-0 rounded-sm" />
                                            <div className="text-left mt-0.5 w-full max-w-[280px]">
                                                {/* <h4 className="text-blue-400 font-bold text-sm mb-1 uppercase tracking-wider">Point 0{i+1}</h4> */}
                                                <p className="text-slate-300 text-sm font-medium leading-relaxed">{item.merida}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-16">
                        <CTAButton
                            text="Build Smarter Workforce Infrastructure"
                            onClick={openEnquiryModal}
                            type="secondary"
                        />
                    </div>
                </div>
            </section>

            {/* WHAT BUSINESSES GAIN */}
            <section className="py-10 bg-slate-50 text-slate-800 relative border-t border-slate-200">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <span className="text-blue-600 font-bold text-xs uppercase block mb-3">Value Delivered</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne text-[#01014C] mb-6">
                            What Businesses Gain By Partnering With Merida HR Consulting?
                        </h2>
                         <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                            Organizations do not partner with Merida HR Consulting simply to outsource hiring. They partner with us to solve workforce inefficiencies, improve operational scalability, reduce recruitment complexity, strengthen payroll governance, and create <a href="/blog/workforce-systems-supporting-bussiness" className="text-blue-600 underline">workforce systems capable of supporting long-term business growth.</a>
                        </p>
                    </div>

                    <div className="space-y-16 max-w-6xl mx-auto">
                        {coreBenefits.map((benefit, i) => {
                            const isImageRight = i % 2 === 0;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6 }}
                                    className="p-8 md:p-12 bg-white rounded-[3rem] shadow-xl border border-slate-200/60"
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
                                        <div className={`space-y-6 flex flex-col justify-between ${isImageRight ? '' : 'lg:order-2'}`}>
                                            <div className="space-y-4">
                                                <h3 className="text-xl md:text-2xl font-bold syne text-[#01014C] leading-snug">{benefit.title}</h3>
                                                <p className="text-slate-600 text-sm leading-relaxed">{benefit.desc}</p>
                                            </div>

                                            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                                                <h4 className="font-bold text-xs uppercase tracking-wider text-blue-600 mb-3">Key Gains Offered</h4>
                                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                    {benefit.points.map((pt, j) => (
                                                        <li key={j} className="flex items-center gap-2 text-xs text-slate-700">
                                                            <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                                            <span>{pt}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className={`overflow-hidden rounded-3xl w-full h-full min-h-[300px] lg:min-h-full border border-slate-200/10 relative shadow-md ${isImageRight ? 'lg:order-2' : ''}`}>
                                            <img
                                                src={benefit.img}
                                                alt={benefit.title}
                                                className="w-full h-full object-cover hover:scale-105 transition-all duration-500"
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="text-center mt-16">
                        <CTAButton
                            text="Build Long-Term Workforce Infrastructure"
                            onClick={openEnquiryModal}
                            type="secondary"
                        />
                    </div>
                </div>
            </section>

            {/* STRATEGIC PILLARS */}
            <section className="py-10 bg-white relative border-t border-slate-100">
                <div className="container mx-auto px-6">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <span className="text-blue-600 font-bold  text-xs uppercase block mb-3">Our Core Philosophy</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne mb-4 text-[#01014C]">
                            The Strategic Pillars Behind Our Workforce Consulting Approach
                        </h2>
                        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                            A structured foundation designed to align talent acquisition with sustainable organizational performance.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {strategicPillars.map((pillar, i) => {
                            const IconComponent = pillar.icon;
                            return (
                                <div key={i} className="p-8 bg-slate-50 border border-slate-200/60 rounded-[2.5rem] hover:bg-white hover:border-blue-500/20 hover:shadow-xl transition-all duration-300 flex flex-col text-left shadow-sm">
                                    <div className="flex justify-between items-start w-full mb-6">
                                        <div className="w-12 h-12 bg-blue-50 text-blue-600 flex items-center justify-center rounded-2xl shadow-sm">
                                            <IconComponent className="w-6 h-6" />
                                        </div>
                                        <span className="text-xs font-bold text-slate-400">0{i + 1}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <h3 className="text-lg md:text-xl font-bold text-[#01014C] leading-snug">{pillar.title}</h3>
                                        <p className="text-slate-600 text-sm leading-relaxed">{pillar.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center">
                        <CTAButton
                            text="Build A Future-Ready Workforce Strategy"
                            onClick={openEnquiryModal}
                        />
                    </div>
                </div>
            </section>

            {/* HOW WE OPERATE DIFFERENTLY (SLIDER) */}
            <section className="py-12 bg-[#2563EB] relative overflow-hidden">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-16">
                        <span className="text-blue-100 font-bold text-xs  uppercase block mb-3">Operational Excellence</span>
                        <h2 className="text-3xl md:text-5xl font-bold syne text-white mb-4">
                            How Merida HR Consulting Operates Differently
                        </h2>
                        <p className="text-blue-100/70 text-sm md:text-base leading-relaxed">
                            Discover our unique methodology that bridges the gap between hiring requirements and business execution.
                        </p>
                    </div>

                    <div className="mb-16">
                        <Slider {...sliderSettings} className="differentiators-slider">
                            {howWeOperate.map((item, i) => (
                                <div key={i} className="px-3 pb-4">
                                    <div className="aspect-square bg-white border border-transparent rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col text-left shadow-md group">
                                       
                                        <div className="flex-1 min-h-0 w-full overflow-hidden bg-slate-50 relative">
                                            <img
                                                src={item.img}
                                                alt={item.title}
                                                className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>

                                        <div className="p-4 space-y-1.5 bg-white z-10">
                                            <h3 className="text-base font-bold text-[#01014C] leading-snug">{item.title}</h3>
                                            <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">{item.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>

                    <div className="text-center mt-12">
                        <CTAButton
                            text="Explore Workforce Solutions"
                            onClick={openEnquiryModal}
                            type="secondary"
                        />
                    </div>
                </div>
                <style>{`
                    .differentiators-slider .slick-track {
                        display: flex !important;
                    }
                    .differentiators-slider .slick-slide {
                        height: auto;
                    }
                    .differentiators-slider .slick-dots li button:before {
                        color: rgba(255, 255, 255, 0.4) !important;
                    }
                    .differentiators-slider .slick-dots li.slick-active button:before {
                        color: #ffffff !important;
                    }
                `}</style>
            </section>

            {/* WHO BENEFITS MOST */}
            <section className="py-10 bg-[#01014C] relative border-b border-white/5">
                <div className="container mx-auto px-6">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <span className="text-blue-400 font-boldtext-xs uppercase block mb-3">Ideal Partnerships</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne mb-4 text-white">
                            Who Benefits Most From Partnering With Merida HR Consulting?
                        </h2>
                        <p className="text-blue-100/70 text-sm md:text-base leading-relaxed">
                            Tailored strategic solutions engineered for enterprises, growing mid-market companies, and fast-scaling startups.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl mx-auto mb-16">
                        {whoBenefits.map((item, i) => {
                            const IconComponent = item.icon;
                            return (
                                <div key={i} className="flex gap-4 items-start p-4 hover:bg-white/5 rounded-2xl transition-all duration-300">
                                    <div className="flex-shrink-0 w-10 h-10 bg-blue-500/20 text-blue-400 flex items-center justify-center rounded-xl">
                                        <IconComponent className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-base font-bold text-white">{item.title}</h3>
                                        <p className="text-blue-100/60 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center">
                        <CTAButton
                            text="Connect With Workforce Strategists"
                            onClick={openEnquiryModal}
                        />
                    </div>
                </div>
            </section>

            {/* BUSINESS OUTCOMES */}
            <section className="py-10 bg-white text-slate-800 relative">
                <div className="container mx-auto px-6">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <span className="text-blue-600 font-bold  text-xs uppercase block mb-3">Model Metrics</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne text-[#01014C] mb-4">
                            Business Outcomes That Define Our Workforce Consulting Model
                        </h2>
                        <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                            We measure our success by the tangible efficiency gains, cost reductions, and scaling achievements of our clients.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {businessOutcomes.map((item, i) => (
                            <div key={i} className="p-8 bg-blue-900 border border-transparent rounded-[2.5rem] hover:bg-blue-800 hover:shadow-2xl transition-all duration-300 text-left">
                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-blue-300 flex-shrink-0" />
                                    {item.title}
                                </h3>
                                <p className="text-blue-100/80 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <CTAButton
                            text="Request Workforce Consultation"
                            onClick={openEnquiryModal}
                        />
                    </div>
                </div>
            </section>

            {/* BOTTOM FINAL CTA */}
            <section className="py-16 bg-white relative overflow-hidden border-t border-slate-100">
                <div className="container mx-auto px-6 relative z-10">
                    <div className=" mx-auto text-center">
                        <div className="w-full rounded-[2rem] overflow-hidden shadow-2xl mb-10 border border-slate-200">
                            <img 
                                src={staffingimg} 
                                alt="Permanent Staffing Company" 
                                className="w-full h-auto object-cover"
                            />
                        </div>
                        <div className="flex justify-center">
                            <CTAButton
                                text="Book Strategic Workforce Consultation"
                                onClick={openEnquiryModal}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

// Simple visual Brain icon representation
const BrainIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1 0-3.12 3 3 0 0 1 0-4.88 2.5 2.5 0 0 1 0-3.12A2.5 2.5 0 0 1 9.5 2z" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 0-3.12 3 3 0 0 0 0-4.88 2.5 2.5 0 0 0 0-3.12A2.5 2.5 0 0 0 14.5 2z" />
    </svg>
);

export default WhyMeridaPage;
